import { faLink } from '@fortawesome/free-solid-svg-icons/faLink';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AtiraEmpty } from '../../components/AtiraEmpty';
import { AtiraIcon } from '../../components/AtiraIcon';
import { AtiraTable } from '../../components/AtiraTable';
import { Flex } from '../../components/Flex';
import { Text } from '../../components/Text';
import { WarningModal } from '../../components/WarningModal';
import { urlTestIDs } from '../../constants/test/url.testIds';
import i18n, { AppLangs } from '../../i18n';
import { AtiraURL } from '../../model/url/AtiraURL';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { urlSliceSelectors } from '../../redux/url/url.selector';
import { shortenUrlActions } from '../../redux/url/url.slice';
import { userSliceSelectors } from '../../redux/user/user.selector';
import { Breakpoints } from '../../theme/Breakpoints';
import { Rounded } from '../../theme/Rounded';
import { Spacing } from '../../theme/Spacing';
import { AtiraToast } from '../../utils/AtiraToast';
import { ShortURLUpdateModal } from './ShortURLUpdateModal';
import { createUserURLsTableColumns } from './createUserURLsTableColumns';

const tableColumnsKeys: (keyof AtiraURL | 'actions')[] = [
  'actions',
  'label',
  'originalURL',
  'shortURL',
  'visits',
  'enabled',
  'createdAt',
];

const TableWrapper = styled.div`
  .ant-table-tbody > tr > td {
    padding: 0 8px !important;
    height: 2.5rem;
  }

  border-radius: ${Rounded.md};
  overflow: hidden;
`;

const PlusIconWrapper = styled(Flex)`
  display: inline-flex;
  width: 1.5rem;
  justify-content: center;
  align-items: center;
  aspect-ratio: 1 / 1;
  background-color: ${({ theme }) => theme.main};
  border-radius: ${Rounded.circle};
  padding-inline: ${Spacing.s};
`;

const Footer = styled(Flex)`
  justify-content: center;
  margin-bottom: ${Spacing.xxxl};

  @media (min-width: ${Breakpoints.TABLET}) {
    margin-bottom: 0;
  }
`;

export const UserURLs: React.FC = () => {
  const [deleteURLLoading, setDeleteURLLoading] = useState(false);
  const [deleteURLModalVisibile, setDeleteURLModalVisibile] = useState(false);
  const [updateURLModalVisible, setUpdateURLModalVisible] = useState(false);
  const [currentURL, setCurrentURL] = useState<AtiraURL | null>(null);

  const { t } = useTranslation();

  const loggedInUserId = useAppSelector(
    userSliceSelectors.selectLoggedInUserId,
  )!;
  const userShortURLs = useAppSelector((s) =>
    urlSliceSelectors.selectShortURLs(s, loggedInUserId),
  );

  const dispatch = useAppDispatch();

  const showDeleteWarning = (url: AtiraURL) => {
    setCurrentURL(url);
    setDeleteURLModalVisibile(true);
  };

  const showUpdateModal = (url: AtiraURL) => {
    setCurrentURL(url);
    setUpdateURLModalVisible(true);
  };

  const deleteURL = async () => {
    try {
      setDeleteURLLoading(true);

      await dispatch(
        shortenUrlActions.deleteURL({
          urlId: currentURL?._id!,
          userId: loggedInUserId,
        }),
      ).unwrap();

      await dispatch(
        shortenUrlActions.getUserShortURLs({ userId: loggedInUserId }),
      ).unwrap();

      AtiraToast.success(t('shorten_url.delete.success'));
    } catch (e: any) {
      AtiraToast.apiError(e);
    } finally {
      setDeleteURLLoading(false);
      setDeleteURLModalVisibile(false);
      setCurrentURL(null);
    }
  };

  const enableURL = async (url: AtiraURL) => {
    try {
      await dispatch(
        shortenUrlActions.enableUserURL({
          urlId: url._id,
          userId: loggedInUserId,
        }),
      ).unwrap();

      await dispatch(
        shortenUrlActions.getUserShortURLs({ userId: loggedInUserId }),
      ).unwrap();

      AtiraToast.success(t('url.enable.success'));
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    }
  };

  const disableURL = async (url: AtiraURL) => {
    try {
      await dispatch(
        shortenUrlActions.disableUserURL({
          urlId: url._id,
          userId: loggedInUserId,
        }),
      ).unwrap();
      AtiraToast.success(t('url.disable.success'));

      await dispatch(
        shortenUrlActions.getUserShortURLs({ userId: loggedInUserId }),
      ).unwrap();
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    }
  };

  const tableColumns = createUserURLsTableColumns(tableColumnsKeys, {
    t,
    disable: disableURL,
    enable: enableURL,
    delete: showDeleteWarning,
    update: showUpdateModal,
  });

  if (!userShortURLs?.length) {
    return (
      <Flex
        alignSelf="center"
        justifyContent="center"
        gap="o"
        height="100%"
        flexDirection="column"
      >
        <Flex
          alignSelf="center"
          justifyContent="center"
          gap="s"
          flexDirection="column"
          width={'80%'}
        >
          <AtiraEmpty
            description={
              <Trans
                i18nKey={'shorten_url.no_urls'}
                components={[
                  <PlusIconWrapper>
                    <AtiraIcon icon={faPlus} color="onMain" size="xs" />
                  </PlusIconWrapper>,
                  <br />,
                ]}
              />
            }
          />
          <Text color="fgMuted" align="center">
            {t('shorten_url.no_urls.description')}
            <AtiraIcon icon={faLink} />
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex width={'100%'} height={'100%'} flexDirection="column">
      <Flex flexDirection="column" marginBottom="m">
        <Flex
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          padding="s"
        >
          <Text>{t('shorten_url.your_short_urls')}</Text>

          <Text>
            {t('common.total')} {userShortURLs.length}
          </Text>
        </Flex>

        <TableWrapper>
          <AtiraTable
            key={JSON.stringify(tableColumns)}
            columns={tableColumns}
            data={userShortURLs}
            direction={i18n.language === AppLangs.AR ? 'rtl' : 'ltr'}
            scroll={{ x: 'max-content', y: 400 }}
            rowHoverable={false}
            pagination={false}
            size="small"
            rowSelection={undefined}
            sticky
          />
        </TableWrapper>
      </Flex>

      <ShortURLUpdateModal
        open={updateURLModalVisible}
        onClose={() => setUpdateURLModalVisible(false)}
        url={currentURL}
        key={`${currentURL?._id}-${currentURL?.isCustom}`}
      />

      <WarningModal
        onClose={() => setDeleteURLModalVisibile(false)}
        open={deleteURLModalVisibile}
        title={t('shorten_url.delete_modal.title')}
        description={t('shorten_url.delete_modal.description')}
        onConfirm={deleteURL}
        loading={deleteURLLoading}
        confirmButtonTestId={urlTestIDs.list.warningModal.confirmButton}
      />

      <Footer>
        <Text align="center">{t('shorten_url.my_urls.footer_paragraph')}</Text>
      </Footer>
    </Flex>
  );
};
